import React, { useState } from 'react';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import { theme } from '../styles/theme';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

function LoginPage({ onNavigate, onLogin }) {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);
  // 'idle' | 'sending' | 'sent'
  const [resendStatus, setResendStatus] = useState('idle');

  const { login, isLoading, error: authError, clearError } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Effacer l'erreur d'authentification si l'utilisateur modifie le formulaire
    if (authError) {
      clearError();
    }
    if (needsActivation) {
      setNeedsActivation(false);
      setResendStatus('idle');
    }
  };

  const handleResendActivation = async () => {
    setResendStatus('sending');
    try {
      await authApi.resendActivation(formData.email);
    } catch (e) {
      // Réponse volontairement générique côté API ; on confirme dans tous les cas.
    }
    setResendStatus('sent');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'L\'adresse email est requise';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Le mot de passe est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const result = await login({
      email: formData.email,
      password: formData.password
    });

    if (result.success) {
      // La redirection est gérée par le contexte et AppContent
      onLogin(result.user); // Pour compatibilité avec l'ancien système
    } else {
      setNeedsActivation(!!result.needsActivation);
      setResendStatus('idle');
    }
    // Les erreurs sont gérées par le contexte AuthContext
  };

  return (
    <div className={theme.classes.page + " flex items-center justify-center p-4"}>
      <div className="max-w-md w-full">
        <div className={theme.classes.card.default + " shadow-xl p-8"}>
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => onNavigate('landing')}
              className={theme.classes.button.ghost + " mb-6 inline-flex items-center"}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </button>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                backgroundColor: `${theme.colors.primary.DEFAULT}33`,
                borderWidth: '1px',
                borderColor: `${theme.colors.primary.DEFAULT}4D`
              }}
            >
              <span className="font-bold text-xl" style={{ color: theme.colors.primary.DEFAULT }}>S</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Connexion</h1>
            <p className="text-gray-400">Accédez à votre espace ShinySmile</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {(errors.submit || authError) && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center space-x-2 text-red-400 animate-slide-in-bottom">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">{errors.submit || authError}</span>
              </div>
            )}

            {needsActivation && resendStatus !== 'sent' && (
              <button
                type="button"
                onClick={handleResendActivation}
                disabled={resendStatus === 'sending'}
                className={theme.classes.button.ghost + " text-sm disabled:opacity-60"}
              >
                {resendStatus === 'sending' ? 'Envoi...' : "Renvoyer l'email d'activation"}
              </button>
            )}

            {resendStatus === 'sent' && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center space-x-2 text-green-400">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">
                  Si un compte non activé existe avec cette adresse, un nouvel email d'activation a été envoyé.
                </span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Adresse email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                    errors.email ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                  }`}
                  placeholder="votre@email.fr"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-12 py-3 bg-black/30 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all ${
                    errors.password ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                  }`}
                  placeholder="Votre mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
              )}
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => setForgotPasswordModalOpen(true)}
                className={theme.classes.button.ghost + " text-sm"}
              >
                Mot de passe oublié ?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full font-semibold py-3 rounded-lg transition-all duration-200 disabled:bg-white/10 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isLoading ? undefined : theme.colors.primary.DEFAULT,
                color: isLoading ? undefined : '#000000'
              }}
              onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = theme.colors.primary.hover)}
              onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = theme.colors.primary.DEFAULT)}
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-400">
              Pas encore de compte ?{' '}
              <button
                onClick={() => onNavigate('register')}
                className="font-medium transition-colors"
                style={{ color: theme.colors.primary.DEFAULT }}
                onMouseEnter={(e) => e.target.style.color = theme.colors.primary.light}
                onMouseLeave={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
              >
                Créer un compte
              </button>
            </p>
          </div>

        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={forgotPasswordModalOpen}
        onClose={() => setForgotPasswordModalOpen(false)}
      />
    </div>
  );
}

export default LoginPage;